import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useCart, useTheme } from '../lib/store';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { HealthStatusBadge } from './HealthStatusBadge';
import {
  LayoutDashboard, Search, ShoppingCart, FileText, Settings, LogOut, Menu, X, Sun, Moon, Home,
  Plus, Truck, Package, FileText as FileText2, QrCode, BarChart3, ClipboardList, IndianRupee,
  ChevronDown, User,
} from 'lucide-react';

// ─── Primary desktop nav — flat 7-item layout (no More menu). Order,
// role gating, and testids intentionally preserved from the previous
// grouped/dropdown layout so any deep link / QA selector keeps working.
// "Price List" is the QR section renamed — same route, same component,
// same role gating (canViewQr).
const PRIMARY_NAV = [
  { path: '/',            label: 'Dashboard',  icon: LayoutDashboard, mobileIcon: Home },
  { path: '/products',    label: 'Products',   icon: Search,          mobileIcon: Search },
  { path: '/cart',        label: 'Quotation',  icon: ShoppingCart,    mobileIcon: Plus,    showBadge: true },
  { path: '/quotations',  label: 'History',    icon: FileText,        mobileIcon: FileText },
];

// Style helpers — kept inline so the whole navbar spec lives in one file.
// The active pill matches the Admin tabs' active state (bg-[#556B2F] text-white).
const navPillClass = (active) =>
  `text-sm font-medium rounded-full h-8 px-3 transition-colors ${
    active
      ? 'bg-[#556B2F] text-white hover:bg-[#465926] hover:text-white'
      : 'text-[#5C6150] hover:text-[#1A1C16] hover:bg-[#556B2F]/8'
  }`;

export default function Layout({ children }) {
  const { user, logout, isAdmin, canManageProducts, canViewQr } = useAuth();  const { items } = useCart();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  // Add a subtle bottom shadow only once the user has scrolled — keeps
  // the top of the page clean while providing depth cue on scroll.
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  // Display name for the user-menu trigger (first char capitalised).
  const displayName = (() => {
    const raw = (user?.email?.split('@')[0] || user?.name || '').trim();
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Account';
  })();

  // ─── Role-based visibility for the two dropdowns.
  // (Rules copied 1:1 from the previous flat nav — no role now sees an
  // item it couldn't see before. Delivery is visible to everyone, QR
  // → renamed Price List, still gated by canViewQr, etc.)
  const showCollect        = !!user?.collections_enabled;
  const showDebtors        = !!user?.collections_uploader && !canManageProducts;
  // My Registrations is hidden for admins — admins use the Admin
  // panel's Walk-ins/Leads tabs, which have wider filters + edit powers.
  const showMyRegistrations = !isAdmin;

  return (
    <div className="min-h-screen bg-[#F9FAF7]" data-testid="app-layout">
      {/* ─── STICKY TOP NAV ─────────────────────────────────────────── */}
      <header
        className={`bg-white border-b border-[#E2E6D9] sticky top-0 z-50 safe-top transition-shadow ${
          scrolled ? 'shadow-[0_2px_8px_-4px_rgba(26,28,22,0.15)]' : ''
        }`}
        data-testid="app-header"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[60px] gap-2">
          {/* Left cluster: hamburger (mobile) + logo + primary nav */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-[#5C6150] shrink-0"
              data-testid="mobile-menu-btn"
              aria-label="Toggle navigation menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Logo mark only — no wordmark at any screen size. Bumped
                to h-14 (was h-12) per visual-edit request; still fits
                inside the h-[60px] navbar with 4 px of breathing room
                so the mobile bottom nav stays untouched. */}
            <div
              className="flex items-center cursor-pointer shrink-0"
              onClick={() => navigate('/')}
              data-testid="nav-logo-btn"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_quick-quote-25/artifacts/lc3jnw3x_Hedaply%20Logo.jpg"
                alt="HEDA"
                className="h-14 w-14 object-contain shrink-0"
                data-testid="nav-logo"
              />
            </div>

            {/* Desktop primary nav — 7 flat items:
                Dashboard · Products · Quotation · History · Delivery ·
                Price List (was QR) · Admin. Delivery is visible to all
                logged-in users (no role gate); Price List gated by
                canViewQr; Admin by canManageProducts. */}
            <nav className="hidden lg:flex items-center gap-1 ml-2" data-testid="desktop-primary-nav">
              {PRIMARY_NAV.map(({ path, label, icon: Icon, showBadge }) => {
                const active = isActive(path);
                return (
                  <Button
                    key={path}
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(path)}
                    className={`relative ${navPillClass(active)}`}
                    data-testid={`nav-${label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {label}
                    {showBadge && items.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#DAA520] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                        {items.length}
                      </span>
                    )}
                  </Button>
                );
              })}

              {/* Delivery — was inside the removed "More" menu. Testid
                  kept as nav-more-delivery so any existing QA selector
                  still resolves. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/delivery')}
                className={navPillClass(isActive('/delivery'))}
                data-testid="nav-more-delivery"
              >
                <Truck className="w-4 h-4 mr-1.5" /> Delivery
              </Button>

              {/* Price List — the QR section relabelled. Route
                  (/qr-codes) and role gate (canViewQr) unchanged, so
                  existing bookmarks and permission logic keep working. */}
              {canViewQr && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/qr-codes')}
                  className={navPillClass(isActive('/qr-codes'))}
                  data-testid="nav-more-qr"
                >
                  <QrCode className="w-4 h-4 mr-1.5" /> Price List
                </Button>
              )}

              {/* Purchase Orders — same role gate as the mobile menu
                  entry (canManageProducts). Placed after Price List. */}
              {canManageProducts && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/purchase-orders')}
                  className={navPillClass(isActive('/purchase-orders'))}
                  data-testid="nav-purchase-orders"
                >
                  <FileText2 className="w-4 h-4 mr-1.5" /> Purchase Orders
                </Button>
              )}

              {canManageProducts && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin')}
                    className={navPillClass(isActive('/admin'))}
                    data-testid="nav-admin"
                  >
                    <Settings className="w-4 h-4 mr-1.5" /> Admin
                  </Button>
                  {/* Health-watchdog badge — only visible on FAILED
                      checks, sits next to Admin as before. */}
                  <HealthStatusBadge />
                </>
              )}
            </nav>
          </div>

          {/* Right cluster: user menu (all utility / personal items live here). */}
          <div className="flex items-center gap-2 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-sm text-[#1A1C16] hover:bg-[#556B2F]/8 rounded-full h-8 px-2 gap-1.5"
                  data-testid="user-menu-btn"
                >
                  <span className="flex items-center justify-center bg-[#556B2F] text-white rounded-full w-6 h-6 text-xs font-semibold">
                    {displayName.charAt(0)}
                  </span>
                  <span className="hidden sm:inline whitespace-nowrap">{displayName}</span>
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52" data-testid="user-menu">
                <DropdownMenuLabel className="text-xs text-[#8F9485] font-normal truncate">
                  {user?.email || 'Signed in'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={() => navigate('/my-analytics')}
                  data-testid="user-menu-my-analytics"
                >
                  <BarChart3 className="w-4 h-4 mr-2" /> My Analytics
                </DropdownMenuItem>
                {showMyRegistrations && (
                  <DropdownMenuItem
                    onClick={() => navigate('/my-registrations')}
                    data-testid="user-menu-my-registrations"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" /> My List
                  </DropdownMenuItem>
                )}
                {showCollect && (
                  <DropdownMenuItem
                    onClick={() => navigate('/my-collections')}
                    data-testid="user-menu-my-collections"
                  >
                    <IndianRupee className="w-4 h-4 mr-2" /> Collect
                  </DropdownMenuItem>
                )}
                {showDebtors && (
                  <DropdownMenuItem
                    onClick={() => navigate('/collections-hub')}
                    data-testid="user-menu-collections-hub"
                  >
                    <FileText2 className="w-4 h-4 mr-2" /> Debtors
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={toggleTheme} data-testid="user-menu-theme">
                  {theme === 'light' ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
                  {theme === 'light' ? 'Dark mode' : 'Light mode'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  className="text-[#B93838] focus:text-[#B93838]"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* ─── Mobile hamburger dropdown (unchanged content, still lists every
            page the user has access to). Only shown when hamburger tapped. */}
        {mobileOpen && (
          <div
            className="lg:hidden border-t border-[#E2E6D9] bg-white p-2 animate-fade-in"
            data-testid="mobile-nav"
          >
            {PRIMARY_NAV.map(({ path, label, icon: Icon, showBadge }) => (
              <Button
                key={path}
                variant="ghost"
                className="w-full justify-start text-sm mb-1"
                onClick={() => { navigate(path); setMobileOpen(false); }}
                data-testid={`mobile-nav-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
                {showBadge && items.length > 0 && (
                  <span className="ml-2 bg-[#DAA520] text-white text-[10px] px-1.5 rounded-full font-bold">{items.length}</span>
                )}
              </Button>
            ))}
            <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/delivery'); setMobileOpen(false); }} data-testid="mobile-nav-delivery">
              <Truck className="w-4 h-4 mr-2" /> Delivery
            </Button>
            {canViewQr && (
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/qr-codes'); setMobileOpen(false); }} data-testid="mobile-nav-qr">
                <QrCode className="w-4 h-4 mr-2" /> Price List
              </Button>
            )}
            <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/my-analytics'); setMobileOpen(false); }} data-testid="mobile-nav-my-analytics">
              <BarChart3 className="w-4 h-4 mr-2" /> My Analytics
            </Button>
            {showMyRegistrations && (
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/my-registrations'); setMobileOpen(false); }} data-testid="mobile-nav-my-registrations">
                <ClipboardList className="w-4 h-4 mr-2" /> My Walk-ins &amp; Leads
              </Button>
            )}
            {user?.collections_enabled && (
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/my-collections'); setMobileOpen(false); }} data-testid="mobile-nav-my-collections">
                <IndianRupee className="w-4 h-4 mr-2" /> Collections
              </Button>
            )}
            {user?.collections_uploader && !canManageProducts && (
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/collections-hub'); setMobileOpen(false); }} data-testid="mobile-nav-collections-hub">
                <FileText2 className="w-4 h-4 mr-2" /> Debtors Upload
              </Button>
            )}
            {canManageProducts && (
              <>
                <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/admin'); setMobileOpen(false); }}>
                  <Settings className="w-4 h-4 mr-2" /> Admin
                </Button>
                <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/stock'); setMobileOpen(false); }}>
                  <Package className="w-4 h-4 mr-2" /> Stock
                </Button>
                <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { navigate('/purchase-orders'); setMobileOpen(false); }}>
                  <FileText2 className="w-4 h-4 mr-2" /> Purchase Orders
                </Button>
              </>
            )}
            <div className="border-t border-[#E2E6D9] mt-2 pt-2 flex items-center justify-between px-2">
              <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-[#5C6150]" data-testid="mobile-theme-toggle">
                {theme === 'light' ? <Moon className="w-4 h-4 mr-1.5" /> : <Sun className="w-4 h-4 mr-1.5" />}
                {theme === 'light' ? 'Dark mode' : 'Light mode'}
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} className="text-[#B93838]" data-testid="mobile-logout">
                <LogOut className="w-4 h-4 mr-1.5" /> Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Main content — sticky header is in-flow, so no fixed top padding is
          required; a small pt-1 gives visual breathing room under the shadow. */}
      <main className="min-h-[calc(100vh-60px)] pb-20 sm:pb-0">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-6">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation Bar — Iter-129: strict 5-slot layout
          (Left1 · Left2 · Plus · Right1 · Right2). Plus button is the
          middle slot so it's always centered. Delivery is intentionally
          NOT shown here (owner directive). Staff sees "My Reg" (walk-ins
          + leads history) in the Admin slot they can't access. */}
      <nav className="mobile-bottom-nav sm:hidden grid grid-cols-5" data-testid="mobile-bottom-nav">
        {(() => {
          const left = [
            { path: '/', label: 'Dashboard', mobileIcon: Home },
            { path: '/products', label: 'Products', mobileIcon: Search },
          ];
          const right = [
            { path: '/quotations', label: 'History', mobileIcon: FileText },
            canManageProducts
              ? { path: '/admin', label: 'Admin', mobileIcon: Settings }
              : { path: '/my-registrations', label: 'My Reg', mobileIcon: ClipboardList },
          ];
          const renderSlot = ({ path, label, mobileIcon: Icon }) => {
            const active = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`flex flex-col items-center justify-center py-1 rounded-lg transition-colors ${active ? 'text-[#556B2F]' : 'text-[#8F9485]'}`}
                data-testid={`bottom-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-0.5 font-medium">{label}</span>
              </button>
            );
          };
          return (
            <>
              {left.map(renderSlot)}
              <button
                onClick={() => navigate('/cart')}
                className="flex items-center justify-center relative"
                data-testid="bottom-nav-quotation"
              >
                <span className="flex items-center justify-center bg-[#556B2F] text-white -mt-4 shadow-lg rounded-full w-14 h-14">
                  <Plus className="w-6 h-6" />
                </span>
                {items.length > 0 && (
                  <span className="absolute -top-2 right-2 bg-[#DAA520] text-white text-[10px] min-w-[16px] h-4 rounded-full flex items-center justify-center font-bold px-1">
                    {items.length}
                  </span>
                )}
              </button>
              {right.map(renderSlot)}
            </>
          );
        })()}
      </nav>
    </div>
  );
}
